import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiSearch, FiCalendar, FiMapPin, FiUsers, FiExternalLink, FiFileText, FiStar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import './JobPosts.scss';

const JobPosts = () => {
  const navigate = useNavigate();
  const [jobPosts, setJobPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [imageUploading, setImageUploading] = useState(false);

  // Enhanced job post fields
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    details: '',
    applyLink: '',
    img: '',
    isVisible: true,
    featured: false,
    location: '',
    salary: '',
    employmentType: 'full-time',
    experienceLevel: 'mid-level',
    department: '',
    skills: [],
    benefits: [],
    applicationDeadline: '',
    company: 'Myelinz',
    contactEmail: 'careers@myelinz.com',
    requirements: '',
    responsibilities: '',
    tags: []
  });

  const fetchJobPosts = useCallback(async () => {
    try {
      setLoading(true);
      const jobPostsRef = collection(db, "recruitmentSystem", "jobPosts", "JobPosts");
      // Use createdAt for query when sorting by updatedAt so docs without updatedAt are still returned
      const queryOrderBy = sortBy === 'updatedAt' ? 'createdAt' : sortBy;
      const q = query(jobPostsRef, orderBy(queryOrderBy, sortOrder));
      const querySnapshot = await getDocs(q);
      let jobs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      if (sortBy === 'updatedAt') {
        const getTime = (job) => {
          const ts = job.updatedAt || job.createdAt;
          return ts?.toDate ? ts.toDate().getTime() : (ts ? new Date(ts).getTime() : 0);
        };
        jobs = [...jobs].sort((a, b) =>
          sortOrder === 'desc' ? getTime(b) - getTime(a) : getTime(a) - getTime(b)
        );
      }
      setJobPosts(jobs);
    } catch (error) {
      console.error('Error fetching job posts:', error);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    fetchJobPosts();
  }, [fetchJobPosts]);

  // Generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ''); // Remove hyphens from start and end
  };

  // Auto-generate slug when title changes (ONLY for new jobs, NEVER when editing)
  // This ensures slugs remain stable for existing job posts to preserve shared URLs
  useEffect(() => {
    // Only auto-generate slug for new job posts (when not editing)
    // Never auto-update slug when editing to preserve existing shared URLs
    if (!editingJob && formData.title) {
      setFormData(prev => {
        // Only generate if slug is empty (user hasn't manually set one)
        if (!prev.slug || prev.slug === '') {
          const newSlug = generateSlug(formData.title);
          return { ...prev, slug: newSlug };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.title, editingJob]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const jobPostsRef = collection(db, "recruitmentSystem", "jobPosts", "JobPosts");
      
      // For new jobs: generate slug if not provided
      // For existing jobs: preserve existing slug (only use formData.slug if manually edited)
      const finalSlug = editingJob 
        ? (formData.slug || editingJob.slug || generateSlug(formData.title))
        : (formData.slug || generateSlug(formData.title));
      
      const jobData = {
        ...formData,
        slug: finalSlug,
        createdAt: editingJob ? editingJob.createdAt : new Date(),
        updatedAt: new Date(),
        skills: formData.skills.filter(skill => skill.trim() !== ''),
        benefits: formData.benefits.filter(benefit => benefit.trim() !== ''),
        tags: formData.tags.filter(tag => tag.trim() !== '')
      };

      if (editingJob) {
        await updateDoc(doc(jobPostsRef, editingJob.id), jobData);
      } else {
        await addDoc(jobPostsRef, jobData);
      }

      setShowModal(false);
      setEditingJob(null);
      resetForm();
      fetchJobPosts();
    } catch (error) {
      console.error('Error saving job post:', error);
    }
  };

  const handleDelete = async (jobId) => {
    try {
      const jobPostsRef = collection(db, "recruitmentSystem", "jobPosts", "JobPosts");
      await deleteDoc(doc(jobPostsRef, jobId));
      setDeleteConfirm(null);
      fetchJobPosts();
    } catch (error) {
      console.error('Error deleting job post:', error);
    }
  };

  const toggleVisibility = async (jobId, currentVisibility) => {
    try {
      const jobPostsRef = collection(db, "recruitmentSystem", "jobPosts", "JobPosts");
      await updateDoc(doc(jobPostsRef, jobId), {
        isVisible: !currentVisibility,
        updatedAt: new Date()
      });
      fetchJobPosts();
    } catch (error) {
      console.error('Error updating visibility:', error);
    }
  };

  const toggleFeatured = async (jobId, currentFeatured) => {
    try {
      const jobPostsRef = collection(db, "recruitmentSystem", "jobPosts", "JobPosts");
      await updateDoc(doc(jobPostsRef, jobId), {
        featured: !currentFeatured,
        updatedAt: new Date()
      });
      fetchJobPosts();
    } catch (error) {
      console.error('Error updating featured status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      details: '',
      applyLink: '',
      img: '',
      isVisible: true,
      featured: false,
      location: '',
      salary: '',
      employmentType: 'full-time',
      experienceLevel: 'mid-level',
      department: '',
      skills: [],
      benefits: [],
      applicationDeadline: '',
      company: 'Myelinz',
      contactEmail: 'careers@myelinz.com',
      requirements: '',
      responsibilities: '',
      tags: []
    });
  };

  const openEditModal = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      slug: job.slug || generateSlug(job.title || ''),
      details: job.details || '',
      applyLink: job.applyLink || '',
      img: job.img || '',
      isVisible: job.isVisible !== undefined ? job.isVisible : true,
      featured: job.featured === true,
      location: job.location || '',
      salary: job.salary || '',
      employmentType: job.employmentType || 'full-time',
      experienceLevel: job.experienceLevel || 'mid-level',
      department: job.department || '',
      skills: job.skills || [],
      benefits: job.benefits || [],
      applicationDeadline: job.applicationDeadline || '',
      company: job.company || 'Myelinz',
      contactEmail: job.contactEmail || 'careers@myelinz.com',
      requirements: job.requirements || '',
      responsibilities: job.responsibilities || '',
      tags: job.tags || []
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingJob(null);
    resetForm();
    setShowModal(true);
  };

  const addArrayItem = (field, value) => {
    if (value.trim() !== '') {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setImageUploading(true);
    try {
      // Create a storage reference
      const fileExtension = file.name.split('.').pop();
      const fileName = `job-post-${Date.now()}.${fileExtension}`;
      const storageRef = ref(storage, `job-posts/images/${fileName}`);
      
      // Upload file to Firebase Storage
      const uploadResult = await uploadBytes(storageRef, file);
      
      // Get download URL after successful upload
      const downloadURL = await getDownloadURL(uploadResult.ref);
      
      // Update form data with the Firebase Storage URL
      setFormData(prev => ({ ...prev, img: downloadURL }));
      
      console.log('Image uploaded to Firebase Storage:', downloadURL);
    } catch (error) {
      console.error('Error uploading image to Firebase Storage:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setImageUploading(false);
    }
  };

  // Filter and search logic
  const filteredJobs = jobPosts.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (job.location && job.location.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (job.department && job.department.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'visible' && job.isVisible) ||
                         (filterStatus === 'hidden' && !job.isVisible);
    
    return matchesSearch && matchesFilter;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="job-posts-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading job posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-posts-management">
      <div className="page-header">
        <div className="header-content">
          <h1>Job Posts Management</h1>
          <p>Manage and monitor all job postings</p>
        </div>
        <button className="btn-primary" onClick={openCreateModal}>
          <FiPlus /> Create New Job Post
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-content">
            <h3>{jobPosts.length}</h3>
            <p>Total Jobs</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon visible">
            <FiEye />
          </div>
          <div className="stat-content">
            <h3>{jobPosts.filter(job => job.isVisible).length}</h3>
            <p>Visible Jobs</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon hidden">
            <FiEyeOff />
          </div>
          <div className="stat-content">
            <h3>{jobPosts.filter(job => !job.isVisible).length}</h3>
            <p>Hidden Jobs</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon recent">
            <FiCalendar />
          </div>
          <div className="stat-content">
            <h3>{jobPosts.filter(job => {
              const jobDate = job.createdAt?.toDate ? job.createdAt.toDate() : new Date(job.createdAt);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return jobDate > weekAgo;
            }).length}</h3>
            <p>This Week</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search jobs by title, details, location, or department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Jobs</option>
            <option value="visible">Visible Only</option>
            <option value="hidden">Hidden Only</option>
          </select>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
            className="filter-select"
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="updatedAt-desc">Last Updated (newest)</option>
            <option value="updatedAt-asc">Last Updated (oldest)</option>
            <option value="title-asc">Title A-Z</option>
            <option value="title-desc">Title Z-A</option>
          </select>
        </div>
      </div>

      {/* Job Posts Table */}
      <div className="jobs-table-container">
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Job Title</th>
              <th>Department</th>
              <th>Location</th>
              <th>Employment Type</th>
              <th>Status</th>
              <th>Featured</th>
              <th>Created</th>
              <th>Last updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentJobs.map((job) => (
              <tr key={job.id}>
                <td>
                  <div className="job-title-cell">
                    <h4>{job.title}</h4>
                    <p>{job.company}</p>
                  </div>
                </td>
                <td>{job.department || 'N/A'}</td>
                <td>
                  <div className="location-cell">
                    <FiMapPin />
                    {job.location || 'Remote'}
                  </div>
                </td>
                <td>
                  <span className={`employment-type ${job.employmentType}`}>
                    {job.employmentType || 'Full-time'}
                  </span>
                </td>
                <td>
                  <button
                    className={`status-toggle ${job.isVisible ? 'visible' : 'hidden'}`}
                    onClick={() => toggleVisibility(job.id, job.isVisible)}
                  >
                    {job.isVisible ? <FiEye /> : <FiEyeOff />}
                    {job.isVisible ? 'Visible' : 'Hidden'}
                  </button>
                </td>
                <td>
                  <button
                    className={`featured-toggle ${job.featured ? 'featured' : ''}`}
                    onClick={() => toggleFeatured(job.id, !!job.featured)}
                    title={job.featured ? 'Unfeature this job' : 'Feature this job'}
                    type="button"
                  >
                    <FiStar />
                    {job.featured ? 'Featured' : 'Not featured'}
                  </button>
                </td>
                <td>{formatDate(job.createdAt)}</td>
                <td>{formatDate(job.updatedAt || job.createdAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon edit"
                      onClick={() => openEditModal(job)}
                      title="Edit Job"
                      type="button"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="btn-icon delete"
                      onClick={() => setDeleteConfirm(job)}
                      title="Delete Job"
                      type="button"
                    >
                      <FiTrash2 />
                    </button>
                    {job.applyLink && (
                      <a
                        href={job.applyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-icon external"
                        title="View Application Link"
                      >
                        <FiExternalLink />
                      </a>
                    )}
                    <button
                      className="btn-icon applications"
                      onClick={() => navigate(`/admin/job-posts/${job.id}/applications`)}
                      title="View Applications"
                      type="button"
                    >
                      <FiFileText />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {currentJobs.length === 0 && (
          <div className="no-jobs">
            <p>No job posts found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingJob ? 'Edit Job Post' : 'Create New Job Post'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="job-form">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Job Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    required
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>

                <div className="form-group full-width">
                  <label>URL Slug {editingJob && <span className="slug-warning">(Edit carefully - changing this will break shared links)</span>}</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => {
                      const newSlug = generateSlug(e.target.value);
                      setFormData(prev => ({ ...prev, slug: newSlug }));
                    }}
                    placeholder="Auto-generated from title (e.g., senior-software-engineer)"
                    className={editingJob ? 'slug-input-editing' : ''}
                  />
                  {!editingJob && (
                    <small className="slug-hint">Slug is auto-generated from the job title. You can edit it manually if needed.</small>
                  )}
                  {editingJob && (
                    <small className="slug-warning-text">⚠️ Changing the slug will break existing shared links. Only edit if absolutely necessary.</small>
                  )}
                </div>

                <div className="form-group">
                  <label>Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="e.g., Engineering, Marketing"
                  />
                </div>

                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., New York, NY or Remote"
                  />
                </div>

                <div className="form-group">
                  <label>Employment Type</label>
                  <select
                    value={formData.employmentType}
                    onChange={(e) => setFormData(prev => ({ ...prev, employmentType: e.target.value }))}
                  >
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="internship">Internship</option>
                    <option value="freelance">Freelance</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Experience Level</label>
                  <select
                    value={formData.experienceLevel}
                    onChange={(e) => setFormData(prev => ({ ...prev, experienceLevel: e.target.value }))}
                  >
                    <option value="entry-level">Entry Level</option>
                    <option value="mid-level">Mid Level</option>
                    <option value="senior-level">Senior Level</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Salary Range</label>
                  <input
                    type="text"
                    value={formData.salary}
                    onChange={(e) => setFormData(prev => ({ ...prev, salary: e.target.value }))}
                    placeholder="e.g., $80,000 - $120,000"
                  />
                </div>

                <div className="form-group">
                  <label>Application Deadline</label>
                  <input
                    type="date"
                    value={formData.applicationDeadline}
                    onChange={(e) => setFormData(prev => ({ ...prev, applicationDeadline: e.target.value }))}
                  />
                </div>

                <div className="form-group full-width">
                  <label>Job Description *</label>
                  <textarea
                    value={formData.details}
                    onChange={(e) => setFormData(prev => ({ ...prev, details: e.target.value }))}
                    required
                    rows="4"
                    placeholder="Detailed job description..."
                  />
                </div>

                <div className="form-group full-width">
                  <label>Requirements</label>
                  <textarea
                    value={formData.requirements}
                    onChange={(e) => setFormData(prev => ({ ...prev, requirements: e.target.value }))}
                    rows="3"
                    placeholder="Required qualifications and skills..."
                  />
                </div>

                <div className="form-group full-width">
                  <label>Responsibilities</label>
                  <textarea
                    value={formData.responsibilities}
                    onChange={(e) => setFormData(prev => ({ ...prev, responsibilities: e.target.value }))}
                    rows="3"
                    placeholder="Key responsibilities and duties..."
                  />
                </div>

                <div className="form-group">
                  <label>Featured Image</label>
                  <div className="image-upload-section">
                    <input
                      type="url"
                      value={formData.img}
                      onChange={(e) => setFormData(prev => ({ ...prev, img: e.target.value }))}
                      placeholder="https://... or upload image below"
                      className="image-url-input"
                    />
                    <div className="image-upload-controls">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        id="image-upload"
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="image-upload" className="upload-btn">
                        {imageUploading ? 'Uploading...' : 'Upload Image'}
                      </label>
                      {formData.img && (
                        <div className="image-preview">
                          <img src={formData.img} alt="Preview" />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, img: '' }))}
                            className="remove-image-btn"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Skills</label>
                  <div className="array-input">
                    <div className="array-items">
                      {formData.skills.map((skill, index) => (
                        <span key={index} className="array-item">
                          {skill}
                          <button type="button" onClick={() => removeArrayItem('skills', index)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="array-input-controls">
                      <input
                        type="text"
                        placeholder="Add skill..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addArrayItem('skills', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Benefits</label>
                  <div className="array-input">
                    <div className="array-items">
                      {formData.benefits.map((benefit, index) => (
                        <span key={index} className="array-item">
                          {benefit}
                          <button type="button" onClick={() => removeArrayItem('benefits', index)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="array-input-controls">
                      <input
                        type="text"
                        placeholder="Add benefit..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addArrayItem('benefits', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Tags</label>
                  <div className="array-input">
                    <div className="array-items">
                      {formData.tags.map((tag, index) => (
                        <span key={index} className="array-item">
                          {tag}
                          <button type="button" onClick={() => removeArrayItem('tags', index)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="array-input-controls">
                      <input
                        type="text"
                        placeholder="Add tag..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addArrayItem('tags', e.target.value);
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isVisible}
                      onChange={(e) => setFormData(prev => ({ ...prev, isVisible: e.target.checked }))}
                    />
                    Make job visible to candidates
                  </label>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, featured: e.target.checked }))}
                    />
                    Feature this job (show in featured section on careers page)
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingJob ? 'Update Job Post' : 'Create Job Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Job Post</h2>
              <button className="close-btn" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this job post?</p>
              <div className="job-preview">
                <h4>{deleteConfirm.title}</h4>
                <p>{deleteConfirm.company} • {deleteConfirm.department || 'N/A'}</p>
              </div>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary">
                Cancel
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="btn-danger">
                Delete Job Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobPosts; 